import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { BACKEND_URL } from '@/utils/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  ChevronLeft, PlayCircle, FileText, Video, BookOpen, CheckCircle2,
  Clock, ArrowRight, BarChart3, StickyNote, Image, Trash2
} from 'lucide-react';

import { toast } from 'sonner';

const COMFORT_EMOJIS = { very_happy: '😄', happy: '🙂', neutral: '😐', uncomfortable: '😟', upset: '😢' };

export default function AdminChildDetailPage() {
  const { childId } = useParams();
  const navigate = useNavigate();
  const [childName, setChildName] = useState('');
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSessions = async () => {
      try {
        const res = await api.get(`/admin/children/${childId}/sessions`);
        setChildName(res.data.child_name);
        setSessions(res.data.sessions);
      } catch (e) { process.env.NODE_ENV === 'development' && console.error(e); } finally { setLoading(false); }
    };
    fetchSessions();
  }, [childId]);

  const viewSessionDetail = async (session) => {
    setSelectedSession(session);
    try {
      const res = await api.get(`/sessions/${session.session_id}/detail`);
      setDetail(res.data);
    } catch { setDetail(null); }
  };

  const deleteSession = async (e, session) => {
    e.stopPropagation();
    if (!window.confirm(`Delete Session #${session.session_number}? This will remove all videos, assessments, storyboards, and associated data.`)) return;
    try {
      await api.delete(`/admin/sessions/${session.session_id}`);
      setSessions(prev => prev.filter(s => s.session_id !== session.session_id));
      if (selectedSession?.session_id === session.session_id) { setDetail(null); setSelectedSession(null); }
      toast.success(`Session #${session.session_number} deleted`);
    } catch { toast.error('Failed to delete session'); }
  };

  const deleteStoryboard = async (e, storyboardId, sessionNumber) => {
    e.stopPropagation();
    if (!window.confirm(`Delete the storyboard for Session #${sessionNumber}?`)) return;
    try {
      await api.delete(`/admin/storyboards/${storyboardId}`);
      setSessions(prev => prev.map(s => s.storyboard_id === storyboardId ? { ...s, has_storyboard: false, storyboard_id: null } : s));
      toast.success('Storyboard deleted');
    } catch { toast.error('Failed to delete storyboard'); }
  };

  if (loading) return <div className="flex items-center justify-center p-12"><img src="/logo.png" alt="Loading" className="w-12 h-12 animate-pulse" /></div>;

  return (
    <div className="max-w-6xl mx-auto animate-fadeIn" data-testid="admin-child-detail">
      <button onClick={() => navigate('/dashboard/admin')} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-4 transition-colors" data-testid="back-to-admin">
        <ChevronLeft className="w-4 h-4" /> Back to Admin
      </button>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-800" style={{ fontFamily: 'Nunito' }}>{childName || 'Child'}'s Sessions</h1>
          <p className="text-slate-500">{sessions.length} sessions total</p>
        </div>
        <Button className="rounded-xl bg-[#1E1B4B] text-white gap-2 font-bold" onClick={() => navigate(`/dashboard/progress/${childId}`)} data-testid="view-progress-charts">
          <BarChart3 className="w-4 h-4" /> View Progress Charts
        </Button>
      </div>

      {/* Sessions List */}
      <div className="space-y-3">
        {sessions.map(s => (
          <Card key={s.session_id} className={`rounded-2xl border-slate-100 shadow-sm cursor-pointer transition-all hover:shadow-md ${selectedSession?.session_id === s.session_id ? 'ring-2 ring-[#22D3EE]' : ''}`} onClick={() => viewSessionDetail(s)} data-testid={`session-card-${s.session_id}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm ${s.status === 'completed' ? 'bg-green-100 text-green-700' : s.status === 'active' ? 'bg-yellow-100 text-yellow-700' : 'bg-slate-100 text-slate-500'}`}>
                    #{s.session_number}
                  </div>
                  <div>
                    <p className="font-semibold text-slate-700 text-sm">Session #{s.session_number}</p>
                    <div className="flex flex-wrap gap-2 mt-0.5">
                      <span className="text-xs text-slate-400"><Clock className="w-3 h-3 inline mr-0.5" />{new Date(s.date || s.created_at).toLocaleDateString()}</span>
                      <Badge variant="outline" className={`rounded-full text-xs ${s.status === 'completed' ? 'border-green-200 text-green-600' : 'border-slate-200 text-slate-500'}`}>{s.status}</Badge>
                      {s.video_count > 0 && <Badge variant="outline" className="rounded-full text-xs"><Video className="w-3 h-3 mr-0.5" />{s.video_count} videos</Badge>}
                      {s.has_storyboard && <Badge variant="outline" className="rounded-full text-xs border-purple-200 text-purple-600"><BookOpen className="w-3 h-3 mr-0.5" />Storyboard</Badge>}
                    </div>
                  </div>
                </div>
                {s.instructor_notes && <StickyNote className="w-4 h-4 text-amber-400 flex-shrink-0" />}
                <div className="flex items-center gap-1 flex-shrink-0">
                  {s.has_storyboard && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-purple-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg"
                      onClick={(e) => deleteStoryboard(e, s.storyboard_id, s.session_number)}
                      title="Delete Storyboard"
                      data-testid={`delete-storyboard-${s.session_id}`}
                    >
                      <BookOpen className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                    onClick={(e) => deleteSession(e, s)}
                    title="Delete Session"
                    data-testid={`delete-session-${s.session_id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
              {s.instructor_notes && (
                <div className="mt-3 p-3 bg-amber-50 rounded-xl">
                  <p className="text-xs font-semibold text-amber-700 mb-1">Instructor Notes</p>
                  <p className="text-sm text-slate-600 whitespace-pre-wrap">{s.instructor_notes}</p>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
        {sessions.length === 0 && <p className="text-center text-slate-400 py-12">No sessions found for this child</p>}
      </div>

      {/* Session Detail Modal */}
      <Dialog open={!!detail} onOpenChange={() => { setDetail(null); setSelectedSession(null); }}>
        <DialogContent className="rounded-3xl max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Nunito' }}>
              Session #{detail?.session?.session_number} — {childName}
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-6 pt-2">
              {/* Visual Schedule */}
              <div>
                <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-2">Visual Schedule</h3>
                {detail.session.if_condition && (
                  <div className="bg-gradient-to-r from-[#ECFCCB] to-[#F7FEE7] rounded-2xl p-4 flex items-center gap-3 mb-3">
                    <div className="flex-1"><span className="text-xs font-bold text-lime-700">IF</span> <span className="font-semibold text-[#1E1B4B]">{detail.session.if_condition}</span></div>
                    <ArrowRight className="w-5 h-5 text-lime-500 flex-shrink-0" />
                    <div className="flex-1 text-right"><span className="text-xs font-bold text-lime-700">THEN</span> <span className="font-semibold text-[#1E1B4B]">{detail.session.then_reward}</span></div>
                  </div>
                )}
                <div className="space-y-2">
                  {(detail.session.skills || []).map((skill, idx) => (
                    <div key={skill.skill_id} className={`flex items-center gap-3 p-3 rounded-xl ${skill.completed ? 'bg-green-50' : 'bg-slate-50'}`}>
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${skill.completed ? 'bg-green-500 text-white' : 'bg-slate-200 text-slate-500'}`}>
                        {skill.completed ? <CheckCircle2 className="w-4 h-4" /> : idx + 1}
                      </div>
                      {skill.icon && <img src={`${BACKEND_URL}/api/uploads/${skill.icon}`} alt="" className="w-8 h-8 rounded-lg object-cover" />}
                      <span className={`text-sm font-medium ${skill.completed ? 'text-green-700' : 'text-slate-600'}`}>{skill.name}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Instructor Notes */}
              {detail.session.instructor_notes && (
                <div>
                  <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-2">Instructor Notes</h3>
                  <div className="p-4 bg-amber-50 rounded-2xl">
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{detail.session.instructor_notes}</p>
                  </div>
                </div>
              )}

              {/* Assessments */}
              {detail.assessments.length > 0 && (
                <div>
                  <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-2">Assessment Summary</h3>
                  {detail.assessments.map(a => (
                    <div key={a.assessment_id} className="p-4 bg-slate-50 rounded-2xl space-y-2">
                      <div className="flex gap-4 text-sm">
                        <span className="font-medium text-slate-600">Intervention Total: <strong className="text-[#22D3EE]">{a.intervention_total}/40</strong></span>
                        <span className="font-medium text-slate-600">Critical Thinking: <strong className="text-purple-500">{a.critical_thinking_total}/5</strong></span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Comfort Ratings */}
              {detail.comfort_ratings.length > 0 && (
                <div>
                  <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-2">Comfort Ratings</h3>
                  <div className="flex flex-wrap gap-2">
                    {detail.comfort_ratings.map((cr, i) => {
                      const skill = (detail.session.skills || []).find(s => s.skill_id === cr.skill_id);
                      return (
                        <div key={`acd-${i}`} className="flex items-center gap-1.5 bg-slate-50 rounded-xl px-3 py-2 text-sm">
                          <span>{COMFORT_EMOJIS[cr.rating] || '😐'}</span>
                          <span className="text-slate-600">{skill?.name || cr.skill_id}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Videos */}
              {detail.videos.length > 0 && (
                <div>
                  <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-2">Video Bank ({detail.videos.length} videos)</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {detail.videos.map(v => (
                      <div key={v.video_id} className="rounded-2xl overflow-hidden bg-slate-900 aspect-video">
                        <video src={`${BACKEND_URL}/api/uploads/${v.filename}`} controls preload="metadata" playsInline onError={(e) => { setTimeout(() => e.target.load(), 1000); }} className="w-full h-full object-cover" data-testid={`video-${v.video_id}`} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Storyboard */}
              {detail.storyboard && detail.storyboard.entries?.length > 0 && (
                <div>
                  <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-2">Storyboard</h3>
                  <div className="space-y-3">
                    {detail.storyboard.entries.map((entry, i) => (
                      <div key={entry.entry_id || i} className="flex items-start gap-3 p-3 bg-purple-50 rounded-xl">
                        <div className="w-6 h-6 rounded-full bg-purple-200 text-purple-700 flex items-center justify-center text-xs font-bold flex-shrink-0">{i + 1}</div>
                        <div className="flex-1">
                          <p className="text-sm text-slate-700">{entry.text || entry.caption}</p>
                          {entry.video_id && (
                            <video src={`${BACKEND_URL}/api/uploads/${entry.filename || ''}`} controls preload="metadata" playsInline className="mt-2 rounded-xl w-full max-w-xs aspect-video bg-slate-900" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Parent Feedback */}
              {detail.feedback.length > 0 && (
                <div>
                  <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-2">Parent Feedback</h3>
                  {detail.feedback.map(fb => (
                    <div key={fb.feedback_id} className="p-4 bg-blue-50 rounded-2xl space-y-1">
                      {fb.session_notes && <p className="text-sm text-slate-700"><strong>Notes:</strong> {fb.session_notes}</p>}
                      {fb.engagement_observations && <p className="text-sm text-slate-700"><strong>Engagement:</strong> {fb.engagement_observations}</p>}
                      {fb.suggestions && <p className="text-sm text-slate-700"><strong>Suggestions:</strong> {fb.suggestions}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
